/**
 * Invite-link handling.
 *
 * An invite is `https://host/#s=<secret>`: the secret lives in the URL
 * *fragment*, which browsers never send to a server, and is exchanged once via
 * same-origin POST for an HttpOnly cookie. The fragment is removed from the
 * address bar with `history.replaceState` before the app renders anything,
 * so the secret cannot leak through a screenshot, a shared URL, the history
 * entry, or a `Referer`-style copy/paste. It is never written to
 * `localStorage` (see plan/README.md security grounding).
 */

/** Fragment parameter carrying the invite/host secret. */
export const INVITE_FRAGMENT_KEY = 's';

/**
 * Reads the secret out of a raw `location.hash`. Exported separately from the
 * DOM-touching `takeInviteSecret` so the parsing is testable.
 */
export function parseFragmentSecret(hash: string): string | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (raw === '') return null;
  const secret = new URLSearchParams(raw).get(INVITE_FRAGMENT_KEY);
  if (secret === null) return null;
  const trimmed = secret.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Consumes the invite secret from the current URL: returns it and strips the
 * fragment from the address bar in the same synchronous step, before any
 * network call or render happens.
 */
export function takeInviteSecret(): string | null {
  const secret = parseFragmentSecret(window.location.hash);
  if (window.location.hash !== '') {
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${window.location.search}`,
    );
  }
  return secret;
}

/**
 * Accepts what a user pasted into the manual join field: a whole invite URL,
 * a bare `#s=…` fragment, or the secret itself. Returns `null` when there is
 * nothing usable.
 */
export function parsePastedInvite(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;

  const hashIndex = trimmed.indexOf('#');
  if (hashIndex >= 0) {
    return parseFragmentSecret(trimmed.slice(hashIndex));
  }
  // A bare secret: reject obvious URLs so a link without a fragment is not
  // silently exchanged as if it were a secret.
  if (trimmed.includes('://') || trimmed.includes('/')) return null;
  return trimmed;
}
